import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Radar, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import './CareerScoresPage.css';
import { FaPrint } from 'react-icons/fa';
import logo from '../../assets/OG-logo-main-learning-scores.png';

const LEVEL_NOTE_META = {
  low:  { label: 'Your Result · Developing',  color: '#ff6b6b' },
  mid:  { label: 'Your Result · Balanced',  color: '#ffd166' },
  high: { label: 'Your Result · Strong', color: '#00cc99' },
};

// Neuroticism is reverse-scored: low = better, high = worse
const LEVEL_NOTE_META_NEUROTICISM = {
  low:  { label: 'Your Result · Strong',     color: '#00cc99' },
  mid:  { label: 'Your Result · Balanced',   color: '#ffd166' },
  high: { label: 'Your Result · Developing', color: '#ff6b6b' },
};

// Register only necessary Chart.js components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  BarElement,
  CategoryScale,
  LinearScale
);

const CareerScoresPage = ({ userId }) => {
  const [visualizations, setVisualizations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMounted, setIsMounted] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
const viewingChildId = location.state?.viewingChildId || queryParams.get('viewingChildId') || null;
const viewingChildName = location.state?.childName || decodeURIComponent(queryParams.get('childName') || '') || null;
  const chartRefs = useRef({});
  const pageRef = useRef(null);

  // Cache visualizations in sessionStorage
  const CACHE_KEY = `visualizations_${userId}_Career`;

 useEffect(() => {
  setIsMounted(true);
  const currentCharts = chartRefs.current; // Capture ref value

  // Track page view in Google Analytics
    if (window.gtag) {
      window.gtag('config', 'G-B8RJQ1KWE4', {
        page_path: location.pathname,
        page_title: 'Career Scores Page',
      });
    }

  return () => {
    setIsMounted(false);
    Object.values(currentCharts).forEach((chart) => chart?.destroy());
  };
}, [location]);

  useEffect(() => {if (!userId) {
      setLoading(false);
      setError('No user ID provided');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setError('Unauthorized: Please log in again.');
      navigate('/login');
      return;
    }

    // Check navigation state first
    if (location.state?.visualizations) {
      if (isMounted) {
        const data = location.state.visualizations;
        setVisualizations(data);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data)); // Cache data
        setLoading(false);
        return;
      }
    }

    // Check sessionStorage cache
    const cachedData = sessionStorage.getItem(CACHE_KEY);
    if (cachedData) {
      if (isMounted) {
        try {
          const data = JSON.parse(cachedData);
          setVisualizations(data);
          setLoading(false);
          return;
        } catch (err) {
          console.error('Error parsing cached data:', err);
          sessionStorage.removeItem(CACHE_KEY); // Clear corrupted cache
        }
      }
    }

    // No data found
    if (isMounted) {
      setError('No visualization data available. Please complete the Career assessment.');
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [userId, navigate, isMounted, location.state]);

   useEffect(() => {
      document.body.classList.add('specific-page-career');
      return () => document.body.classList.remove('specific-page-career');
    });
    
  const handlePrint = () => {
    window.print();
  };

  // Memoized radar chart options
  const radarOptions = useMemo(
  () => (chartKey) => ({
    scales: {
      r: {
        beginAtZero: true,
        suggestedMax: chartKey === 'cognitiveAbilities' ? 10 : 5,
        ticks: { 
          stepSize: 1, 
          color: 'white', 
          backdropColor: 'transparent', 
          font: { 
            size: window.innerWidth <= 850 ? 11 : 16   // Smaller font on mobile
          } 
        },
        pointLabels: {
          display: true,
          color: 'white',
          font: { 
            size: window.innerWidth <= 850 ? 12 : 16   // Critical: smaller labels on mobile
          },
          padding: window.innerWidth <= 850 ? 5 : 20,
          backdropColor: 'transparent',
          callback: (label) =>
            label && typeof label === 'string'
              ? label.length > (window.innerWidth <= 850 ? 35 : 50)
                ? label.substring(0, window.innerWidth <= 850 ? 32 : 47) + '...'
                : label
              : 'Unknown',
        },
        grid: { color: 'rgba(255, 255, 255, 0.3)', circular: false },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (context) => `${context.dataset.label}: ${context.raw.toFixed(2)}` } },
    },
    layout: { padding: 0 },
    maintainAspectRatio: false,
  }),
  []
);

  // Memoized bar chart options
 const barOptions = useMemo(
  () => ({
    indexAxis: 'y',
    scales: {
      x: {
        beginAtZero: true,
        suggestedMax: 5,
        ticks: { 
          color: 'white', 
          font: { size: window.innerWidth <= 850 ? 12 : 16 }, 
          stepSize: 1 
        },
      },
      y: {
        ticks: {
          color: (context) => {
            const data = context.chart.data.datasets[0].data;
            const index = context.index;
            const otherIndex = index === 0 ? 1 : 0;
            return data[index] > data[otherIndex] ? 'white' : 'rgba(255, 255, 255, 0.5)';
          },
          font: () => ({ 
            size: window.innerWidth <= 850 ? 12 : 16,   // Smaller + bolder winner
            weight: 'normal' 
          }),
          padding: window.innerWidth <= 850 ? 8 : 5,
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
        },
        grid: { display: false },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (context) => `${context.label}: ${context.raw.toFixed(2)}` } },
    },
    maintainAspectRatio: false,
    layout: { padding: { left: 15, right: 15, top: 10, bottom: 5 } },
  }),
  []
);

  const renderSortedScoresTable = (data) => {
    if (!data?.labels || !data?.datasets?.[0]?.data || !data?.datasets?.[0]?.totalScores) {
      return <p style={{ color: 'white', fontSize: '24px' }}>No data available for table.</p>;
    }

    const scores = data.labels
      .map((label, index) => ({
        _id: label,
        averageScore: data.datasets[0].data[index] || 0,
        totalScore: data.datasets[0].totalScores[index] || 0,
        facetNote: data.datasets[0].facetNote?.[index] || null,
      }))
      .sort((a, b) => b.averageScore - a.averageScore);

    return (
      <table
        className="scores-table"
        style={{
          width: '100%',
          color: 'white',
          fontSize: '14px',
          borderCollapse: 'collapse',
          borderRadius: '10px',
          background: 'linear-gradient(to right, #0b1021, rgb(30, 37, 58))',
        }}
      >
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '15px' }}>Facet</th>
            <th style={{ textAlign: 'right', padding: '15px' }}>Average Score</th>
            <th style={{ textAlign: 'right', padding: '15px' }}>Total Score</th>
          </tr>
        </thead>
        <tbody>
         {scores.map((score) => {
  // facetNote is now { general, level, levelKey } (or null).
  // Guard against legacy string shape from older cached data.
  const noteData =
    typeof score.facetNote === 'string'
      ? { general: score.facetNote.trim() || null, level: null, levelKey: null }
      : score.facetNote || null;

  const hasGeneral = !!noteData?.general?.trim();
  const hasLevel = !!noteData?.level?.trim();

  const isNeuroticism = /neuroticism/i.test(String(score._id || ''));
  const metaTable = isNeuroticism ? LEVEL_NOTE_META_NEUROTICISM : LEVEL_NOTE_META;
  const levelMeta = noteData?.levelKey ? metaTable[noteData.levelKey] : null;

  return (
    <React.Fragment key={score._id || Math.random()}>
      <tr>
        <td style={{ padding: '8px 10px' }}>{score._id || 'Unknown'}</td>
        <td style={{ textAlign: 'right', padding: '8px 10px' }}>
          {score.averageScore != null ? Number(score.averageScore).toFixed(1) : 'N/A'}
        </td>
        <td style={{ textAlign: 'right', padding: '8px 10px' }}>
          {typeof score.totalScore === 'number' ? score.totalScore.toFixed(1) : 'N/A'}
        </td>
      </tr>
      {(hasGeneral || hasLevel) && (
        <tr>
          <td colSpan="3" style={{ padding: '0 10px 10px' }}>
            {hasGeneral && (
              <div style={{ color: '#ffffffcc', fontSize: '12px', marginBottom: hasLevel ? '8px' : 0 }}>
                {noteData.general}
              </div>
            )}
            {hasLevel && levelMeta && (
              <div
                style={{
                  padding: '8px 10px',
                  background: `${levelMeta.color}18`,
                  border: `1px solid ${levelMeta.color}55`,
                  borderRadius: '6px',
                }}
              >
                <div
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: levelMeta.color,
                    marginBottom: '3px',
                  }}
                >
                  {levelMeta.label}
                </div>
                <div style={{ fontSize: '12px', color: '#ddd' }}>
                  {noteData.level}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
})}
        </tbody>
      </table>
    );
  };

  const renderChart = (key, title, data, type, additionalInfo = null) => {
    const Component = type === 'radar' ? Radar : Bar;
    const options = type === 'radar' ? radarOptions(key) : barOptions;
    const height = type === 'radar' ? '500px' : '250px';
    const width = type === 'radar' ? '850px' : '450px';
    const tableWidth = type === 'radar' ? '700px' : '400px';

    return (
      <div className="chart-section" style={{ margin: type === 'radar' ? '20px auto' : '10px auto 50px', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <h3 className="chart-heading" style={{ color: '#028be8', margin: type === 'radar' ? '40px 0 50px' : '50px 0 40px', fontSize: '21px' }}>
          {title}
        </h3>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: type === 'radar' ? '20px' : '10px',
            width: '100%',
            maxWidth: type === 'radar' ? '1800px' : '1300px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div className="chart-container" style={{ height, width }}>
            <Component
              ref={(el) => {
                if (el) chartRefs.current[key] = el.chart;
                else delete chartRefs.current[key];
              }}
              data={data}
              options={options}
            />
          </div>
          <div style={{ width: tableWidth, maxWidth: tableWidth }}>
            {renderSortedScoresTable(data)}
            {additionalInfo && (
              <p style={{ color: 'white', fontSize: '16px', marginTop: '10px' }}>{additionalInfo}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!visualizations) return <div>No visualization data available.</div>;

  return (
          <div className='careerscores'>
    <div ref={pageRef} style={{ padding: '0', maxWidth: '2000px', margin: '0 auto' }}>
      <header className="assessment-header-scores">
        <img src={logo} className="logo-adjustment-score" alt="StudyStyle Logo" />
        <h1 className="score-heading">FutureReady Profile Index</h1>
        <h2 style={{ color: 'white', fontSize: '12px', marginBottom: '15px' }}>
          Personalities, Motivations, and Capabilities
        </h2>
        <div className="header-buttons">
          <button
            className="return-dashboard-cross"
onClick={() => navigate('/CareerDashboard', { 
  state: { 
    fromCareerScores: true,
    ...(viewingChildId && { viewingChildId, childName: viewingChildName }),
  } 
})}            
title="Return to Dashboard"
          />
          <button className="print-button" onClick={handlePrint} title="Download as PDF" disabled={loading}>
            <FaPrint size={35} />
          </button>
        </div>
      </header>
      <div className="content-container">
        {visualizations.careerInterest &&
          renderChart('careerInterest', 'Career Interests & Personality', visualizations.careerInterest, 'radar')}
        {visualizations.valuesWorkPreferences &&
          renderChart('valuesWorkPreferences', 'Working Preferences & Values', visualizations.valuesWorkPreferences, 'radar')}
        {visualizations.motivation &&
          renderChart('motivation', 'Intrinsic vs. Extrinsic Motivation', visualizations.motivation, 'bar')}
          <h5 className="motivation">
            <span style={{ fontWeight: 'bold'}}>Balanced Intrinsic & Extrinsic:</span> A balanced result suggests you are flexible and responsive across a range of environments, engaging fully when you find tasks meaningful while also valuing structured goals, feedback, and rewards. You can maintain this strength by choosing opportunities that challenge you personally while offering recognition for effort and achievement.
        </h5>
        {visualizations.bigFive &&
          renderChart('bigFive', 'Big Five Personality Traits', visualizations.bigFive, 'radar')}
        {visualizations.emotionalIntelligence &&
          renderChart('emotionalIntelligence', 'Emotional Intelligence', visualizations.emotionalIntelligence, 'radar')}
          <h5 className="EQ">
            <span style={{ fontWeight: 'bold' }}>High EQ (scoring 60-75):</span> Strong emotional intelligence, excellent self-awareness, regulation, and social skills.
          </h5>
          <h5 className="EQ">
            <span style={{ fontWeight: 'bold' }}>Moderate EQ (scoring 45-59):</span> Good EQ but areas for improvement in emotional control or empathy.
          </h5>
          <h5 className="EQ">
            <span style={{ fontWeight: 'bold' }}>Low EQ (scoring 45 or less):</span> Needs development in self-awareness, managing emotions, or social interactions.
          </h5>
      </div>
    </div>
    </div>
  );
};

export default CareerScoresPage;